/** Shapes of the Phase 3 form responses (apps/api/src/services/*). Prices exist only on office views. */
import type { SignatureForm, SignatureRole } from '@wo/shared';

export interface PartLine {
  id: number;
  clientId: string | null;
  qty: string;
  partNo: string | null;
  replacedPartNo: string | null;
  description: string;
  source: 'MECHANIC' | 'OFFICE' | 'VOICE_SUGGESTED';
  lateAdd: boolean;
  removed: boolean;
  removedReason: string | null;
  createdAt: string;
  // office only
  stock?: boolean;
  cost?: string | null;
  list?: string | null;
  sell?: string | null;
  extended?: string | null;
  removedAt?: string | null;
}

export interface PartsList {
  noPartsUsed: boolean;
  noPartsUsedAt?: string | null;
  header?: {
    date: string;
    so: number;
    purchaseOrder: string | null;
    salesRep: string | null;
    customerPo: string | null;
    customerName: string;
    contactName: string | null;
    contactPhone: string | null;
    makeModel: string;
    serialNumber: string;
  };
  lines: PartLine[];
  total?: string;
}

export interface Sig {
  id: number;
  form: SignatureForm;
  role: SignatureRole;
  signerName: string;
  fileId: number;
  signedAt: string;
  statementVersion: number | null;
  offline: boolean;
}
export interface LatestSigs {
  technician: Sig | null;
  customer: Sig | null;
}

export interface FieldReportView {
  so: number;
  customer: { name: string; phone: string | null };
  equipment: { make: string; model: string; serialNumber: string; equipmentNo: string | null; hourMeter: string | null };
  customerPo?: string | null;
  billingType?: string | null;
  machineLocation: string | null;
  problemDescription: string;
  workPerformed: { id: number; at: string; by: string; body: string; corrections: { id: number; at: string; by: string; body: string }[] }[];
  lubricants: { eng: string | null; trans: string | null; hyd: string | null; coolant: string | null; other: string | null };
  mileage: string | null;
  meals: string | null;
  motel: string | null;
  labor: { lines: { id: number; mechanic: string; start: string; finish: string | null; hours: number | null }[]; totalHours: number };
  certification: { version: number; text: string };
  signatures: LatestSigs;
}

export interface LubeView {
  brandName: string;
  so: number;
  header: {
    customerName: string;
    customerEquipmentNo: string | null;
    make: string;
    model: string;
    serialNumber: string;
    hours: string | null;
    machineLocation: string | null;
    typeOfService: string | null;
    nextService: string | null;
  };
  items: { item: string; checked: boolean; changed: boolean }[];
  lubricants: { lubricant: string; otherName: string; gallons: string | null }[];
  remarks: string | null;
  submittedAt: string | null;
  missing: string[];
  certification?: { version: number; text: string };
  signatures?: LatestSigs;
}

export interface Inspection {
  id: number;
  workOrderId: number;
  phase: 'OUT' | 'IN';
  hours: string | null;
  fuelLevel: string | null;
  fuelGallonsAdded: string | null;
  defLevel: string | null;
  defJugsAdded: string | null;
  cleanliness: 'CLEAN' | 'ADDITIONAL_TIME' | null;
  operatorsManual: boolean | null;
  attachments: string[];
  initials: string | null;
  notes: string | null;
  items: { item: string; status: 'OK' | 'ISSUE' | null; comment: string | null }[];
  photos: { slot: string; filled: boolean; fileId: number | null; capturedAt: string | null }[];
  submittedAt: string | null;
  missing: string[];
}

export interface Used {
  steps: number;
  fraction: string;
}

export interface RentalView {
  phase: 'OUT' | 'IN';
  controls: string[];
  levels: string[];
  rental: {
    id: number;
    agreementNo: string | null;
    checkout: { id: number; number: number } | null;
    checkin: { id: number; number: number } | null;
    outAt: string | null;
    inAt: string | null;
  } | null;
  out: Inspection | null;
  in: Inspection | null;
  comparison: {
    hoursUsed: string | null;
    fuelUsed: Used | null;
    defUsed: Used | null;
    newDamage: { item: string; outStatus: string | null; inStatus: string; comment: string | null; billing: 'BILLED' | 'WAIVED' | null }[];
  } | null;
  signatures: LatestSigs;
  openRentals?: { id: number; checkoutNumber: number | null; outAt: string | null }[];
}

export interface SignaturesView {
  signatureForm: SignatureForm;
  latest: LatestSigs;
  certification: { version: number; text: string };
  customerNotPresentReason: string | null;
  history: Sig[];
}

/** "3/8 tank" style text for a gauge difference. */
export function usedText(u: Used | null | undefined): string {
  if (!u) return '—';
  if (u.steps === 0) return 'Same as check-out';
  return u.steps > 0 ? `${u.fraction} tank used` : `${u.fraction.replace('-', '')} tank more than check-out`;
}
